import {
  ClaimAppeal,
  ClaimAppealLetter,
  EdiObject,
} from "../../../../types/claims";
import { getEdiObjectTransactionDate } from "./ediObjectHelpers";

/** One card in the Claim timeline. `index` on an EDI item is its position in the
 * sorted EDI list, which is what ClaimPreview's selected tab points at. */
export type ClaimTimelineItem =
  | { kind: "edi"; ediObj: EdiObject; index: number }
  | { kind: "letter"; letter: ClaimAppealLetter }
  | { kind: "appeal"; appeal: ClaimAppeal }
  /** A remit of the corrected claim, read-only on the original (REV-2629); `index` is its place in `linkedRemits`. */
  | { kind: "linkedRemit"; ediObj: EdiObject; fromClaimNumber: string; index: number }
  | { kind: "letterPlaceholder" };

/** The corrected claim's remits, shown on its original's timeline. */
export interface LinkedRemits {
  fromClaimNumber: string;
  remits: EdiObject[];
}

const getItemDate = (item: ClaimTimelineItem): Date | null => {
  if (item.kind === "edi" || item.kind === "linkedRemit")
    return getEdiObjectTransactionDate(item.ediObj);
  if (item.kind === "letter") {
    const parsed = new Date(item.letter.generatedAt);
    return isNaN(parsed.getTime()) ? null : parsed;
  }
  if (item.kind === "appeal") {
    // A civil date (YYYY-MM-DD), parsed as UTC midnight like the EDI dates
    const parsed = new Date(item.appeal.actionOn);
    return isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

const pad = (n: number) => String(n).padStart(2, "0");

/** The item's calendar day as YYYY-MM-DD. EDI and appeal dates are civil dates
 * (UTC midnight); a letter's is a moment, read in the viewer's timezone. */
const getItemDay = (item: ClaimTimelineItem, date: Date): string =>
  item.kind === "letter"
    ? `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
    : date.toISOString().slice(0, 10);

/**
 * The claim's single timeline letter: the earliest generated one (letters with no
 * valid date only win when none has one), summarising all of them. It counts every
 * letter as a version, and is available when the viewer can open any of them.
 */
export const getFirstAppealLetter = (
  appealLetters: ClaimAppealLetter[],
): ClaimAppealLetter | null => {
  if (appealLetters.length === 0) return null;
  let first: ClaimAppealLetter | null = null;
  let firstTime = Infinity;
  for (const letter of appealLetters) {
    const time = new Date(letter.generatedAt).getTime();
    const sortTime = isNaN(time) ? Infinity : time;
    if (!first || sortTime < firstTime) {
      first = letter;
      firstTime = sortTime;
    }
  }
  return {
    ...(first as ClaimAppealLetter),
    versionCount: appealLetters.length,
    available: appealLetters.some((letter) => letter.available),
  };
};

/**
 * Merge the (already sorted) EDI objects with the claim's appeal letters,
 * recorded appeals (one Appealed card each) and, on an original claim, the
 * corrected claim's remits.
 *
 * Claims (837) stay first, as before; everything else is in date order by
 * calendar day, with undated items after the dated ones. On the same day a letter
 * comes before the Appealed card (an appeal date has no time). A claim flagged as
 * having an appeal letter that none of its chats can account for gets a
 * placeholder card at the end: its generation date is unknown.
 */
export const buildClaimTimeline = (
  sortedEdiObjects: EdiObject[],
  appealLetters: ClaimAppealLetter[],
  hasAppealLetterFlag: boolean,
  appeals: ClaimAppeal[] = [],
  linked: LinkedRemits | null = null,
): ClaimTimelineItem[] => {
  const items: ClaimTimelineItem[] = [
    ...sortedEdiObjects.map(
      (ediObj, index): ClaimTimelineItem => ({ kind: "edi", ediObj, index }),
    ),
    ...appealLetters.map(
      (letter): ClaimTimelineItem => ({ kind: "letter", letter }),
    ),
    ...appeals.map((appeal): ClaimTimelineItem => ({ kind: "appeal", appeal })),
    ...(linked
      ? linked.remits.map(
          (ediObj, index): ClaimTimelineItem => ({
            kind: "linkedRemit",
            ediObj,
            fromClaimNumber: linked.fromClaimNumber,
            index,
          }),
        )
      : []),
  ];

  const isClaim = (item: ClaimTimelineItem) =>
    item.kind === "edi" && item.ediObj.objectType === "CLAIM";

  // Array.prototype.sort is stable, so EDI objects keep their existing relative order on ties
  items.sort((a, b) => {
    if (isClaim(a) !== isClaim(b)) return isClaim(a) ? -1 : 1;

    const dateA = getItemDate(a);
    const dateB = getItemDate(b);
    if (!dateA && !dateB) return 0;
    if (!dateA) return 1;
    if (!dateB) return -1;
    const dayA = getItemDay(a, dateA);
    const dayB = getItemDay(b, dateB);
    if (dayA !== dayB) return dayA < dayB ? -1 : 1;
    if (a.kind === "letter" && b.kind === "appeal") return -1;
    if (a.kind === "appeal" && b.kind === "letter") return 1;
    return dateA.getTime() - dateB.getTime();
  });

  if (hasAppealLetterFlag && appealLetters.length === 0) {
    items.push({ kind: "letterPlaceholder" });
  }

  return items;
};
